// import { GetInfinitePagesInterface } from '@interfaces/index';
import type { OrderLazyPropsInterface } from '@interfaces/order'
import { getSessionTokenOrRedirect as getSessionTokenV4OrRedirect } from '@libs/shopify/appBridge'
// import { object } from 'prop-types';
import { csrfToken } from '@rails/ujs'
import { app } from '@utils/app-bridge'
import {
  CLIENT_AUTH_REDIRECT_ERROR,
  getSessionTokenOrRedirect,
  handleShopifyAuthFailure,
  normalizeReauthStatus,
} from '@utils/shopifyAuthFailure'
import axios, {
  type AxiosError,
  type AxiosResponse,
  type CancelTokenSource,
} from 'axios'
import {
  type UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query'
// import * as ACTION_TYPES from '@stores/actions/action_types'
import type { QueryFunctionContext } from 'react-query/types/core/types'

export type QueryKeyT = [string, object | undefined]

// Helper function để tạo query key nhất quán
export const createQueryKey = (
  url: string | null,
  params?: object,
): QueryKeyT => {
  // url null chỉ xảy ra khi query bị disable (enabled: !!url).
  return [url as string, params]
}

// Quản lý CancelToken cho từng query
const cancelTokenMap = new Map<string, CancelTokenSource>()

// Helper function để cancel request cho một URL cụ thể
export const cancelRequest = (url: string) => {
  const token = cancelTokenMap.get(url)
  if (token) {
    token.cancel('Request cancelled')
    cancelTokenMap.delete(url)
  }
}

// Helper function để cancel tất cả requests
export const cancelAllRequests = () => {
  for (const token of cancelTokenMap.values()) {
    token.cancel('All requests cancelled')
  }
  cancelTokenMap.clear()
}

// Debug function để xem tất cả pending requests
export const getPendingRequests = () => {
  return Array.from(cancelTokenMap.keys())
}

/*
Ví dụ sử dụng để test cancel functionality:

// Trong component
const { data, isLoading } = useFetch('/api/orders', { page: 1 });
const updateOrder = useUpdate('/api/orders', { page: 1 });

// Khi user thực hiện mutation, các requests đang pending sẽ được cancel
const handleUpdate = () => {
  updateOrder.mutate(newData);
};

// Để debug
console.log('Pending requests:', getPendingRequests());
cancelAllRequests(); // Cancel tất cả requests
*/

// export const CancelToken = axios.CancelToken;
export const CancelToken = axios.CancelToken
export let cancelToken: CancelTokenSource | undefined
type AppBridgeApp = Parameters<typeof getSessionTokenOrRedirect>[0]
export const getSessionToken = async (app: AppBridgeApp | null) => {
  if (__E2E__) {
    return csrfToken()
  }
  // mock-bridge only implements App Bridge v4 (`shopify.idToken()`); v3 getSessionToken never resolves.
  if (import.meta.env.VITE_MOCK_BRIDGE_ENABLED) {
    return await getSessionTokenV4OrRedirect()
  }
  return await getSessionTokenOrRedirect(app as AppBridgeApp)
}
export const create_cancel = () => {
  cancelToken = CancelToken.source()
}

const buildClientAuthRedirectError = (
  responseUrl: string,
  reauthStatus?: string | null,
) => {
  const authError = {
    type: CLIENT_AUTH_REDIRECT_ERROR,
    message: `${CLIENT_AUTH_REDIRECT_ERROR}: ${responseUrl}`,
  }
  handleShopifyAuthFailure(authError, reauthStatus)
  return authError
}

const ensureNotAuthRedirect = <T>(response: AxiosResponse<T>) => {
  const responseUrl =
    (response.request as { responseURL?: string } | undefined)?.responseURL ||
    ''

  if (responseUrl.includes('/auth/install')) {
    throw buildClientAuthRedirectError(responseUrl)
  }

  return response
}

const normalizeAxiosError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    throw error
  }

  const responseUrl =
    (error.response?.request as { responseURL?: string } | undefined)
      ?.responseURL ||
    (error.request as { responseURL?: string } | undefined)?.responseURL ||
    ''

  if (responseUrl.includes('/auth/install')) {
    throw buildClientAuthRedirectError(responseUrl)
  }

  if (error.response?.status === 401) {
    const reauthStatus = normalizeReauthStatus(
      error.response.headers?.['x-reauth-status'] as string | undefined,
    )
    throw buildClientAuthRedirectError(
      responseUrl || error.config?.url || '',
      reauthStatus,
    )
  }

  throw error
}

export const api = {
  get: async <T>(url: string, params?: object) => {
    const sessionToken = await getSessionToken(app)
    const response = await axios.get<T>(url, {
      headers: {
        'Content-Type': 'application/json',
        // "X-CSRF-Token": csrfToken(),
        Accept: 'application/json',
        Authorization: `Bearer ${sessionToken}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
      ...params,
      data: {},
    })
    return ensureNotAuthRedirect(response)
  },
  // Method mới để hỗ trợ GET với response HTML
  get_html: async <T>(url: string, params?: object) => {
    const sessionToken = await getSessionToken(app)
    const response = await axios.get<T>(url, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/html',
        Authorization: `Bearer ${sessionToken}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
      ...params,
      data: {},
    })
    return ensureNotAuthRedirect(response)
  },
  post_data: async <T>(url: string, data: unknown) => {
    try {
      const sessionToken = await getSessionToken(app)
      const csrfTokenValue = csrfToken()
      const response = await axios.post<T>(url, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(csrfTokenValue && { 'X-CSRF-Token': csrfTokenValue }),
          Accept: 'application/json',
          Authorization: `Bearer ${sessionToken}`,
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      return ensureNotAuthRedirect(response)
    } catch (error) {
      return normalizeAxiosError(error)
    }
  },
  post: async <T>(url: string, data: unknown) => {
    try {
      const sessionToken = await getSessionToken(app)
      const csrfTokenValue = csrfToken()
      const response = await axios.post<T>(url, data, {
        headers: {
          'Content-Type': 'application/json',
          ...(csrfTokenValue && { 'X-CSRF-Token': csrfTokenValue }),
          Accept: 'application/json',
          Authorization: `Bearer ${sessionToken}`,
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      return ensureNotAuthRedirect(response)
    } catch (error) {
      return normalizeAxiosError(error)
    }
  },
  // Method mới để hỗ trợ response HTML cho POST
  post_html: async <T>(url: string, data: unknown) => {
    const sessionToken = await getSessionToken(app)
    const csrfTokenValue = csrfToken()
    const response = await axios.post<T>(url, data, {
      headers: {
        'Content-Type': 'application/json',
        ...(csrfTokenValue && { 'X-CSRF-Token': csrfTokenValue }),
        Accept: 'text/html',
        Authorization: `Bearer ${sessionToken}`,
      },
    })
    return ensureNotAuthRedirect(response)
  },
  patch: async <T>(url: string, data: unknown) => {
    try {
      const sessionToken = await getSessionToken(app)
      const csrfTokenValue = csrfToken()

      // Tạo CancelToken và lưu vào map
      const cancelTokenSource = CancelToken.source()
      cancelTokenMap.set(url, cancelTokenSource)

      const response = await axios.patch<T>(url, data, {
        headers: {
          'Content-Type': 'application/json',
          ...(csrfTokenValue && { 'X-CSRF-Token': csrfTokenValue }),
          Accept: 'application/json',
          Authorization: `Bearer ${sessionToken}`,
          'X-Requested-With': 'XMLHttpRequest',
        },
        cancelToken: cancelTokenSource.token,
      })

      // Xóa CancelToken khỏi map sau khi request hoàn thành
      cancelTokenMap.delete(url)

      return ensureNotAuthRedirect(response)
    } catch (err) {
      // Xóa CancelToken khỏi map khi có lỗi
      cancelTokenMap.delete(url)

      const error = err as Error
      console.error(`Download error: ${error.message}`)
      throw error // Re-throw error để React Query xử lý
    }
  },
  // Method mới để hỗ trợ response HTML cho PATCH
  patch_html: async <T>(url: string, data: unknown) => {
    try {
      const sessionToken = await getSessionToken(app)
      const csrfTokenValue = csrfToken()

      // Tạo CancelToken và lưu vào map
      const cancelTokenSource = CancelToken.source()
      cancelTokenMap.set(url, cancelTokenSource)

      const response = await axios.patch<T>(url, data, {
        headers: {
          'Content-Type': 'application/json',
          ...(csrfTokenValue && { 'X-CSRF-Token': csrfTokenValue }),
          Accept: 'text/html',
          Authorization: `Bearer ${sessionToken}`,
        },
        cancelToken: cancelTokenSource.token,
      })

      // Xóa CancelToken khỏi map sau khi request hoàn thành
      cancelTokenMap.delete(url)
      return ensureNotAuthRedirect(response)
    } catch (err) {
      // Xóa CancelToken khỏi map khi có lỗi
      cancelTokenMap.delete(url)

      const error = err as Error
      console.error(`Download error: ${error.message}`)
      throw error // Re-throw error để React Query xử lý
    }
  },
  // Method mới để hỗ trợ PUT với response HTML
  put_html: async <T>(url: string, data: unknown) => {
    const sessionToken = await getSessionToken(app)
    const csrfTokenValue = csrfToken()
    const response = await axios.put<T>(url, data, {
      headers: {
        'Content-Type': 'application/json',
        ...(csrfTokenValue && { 'X-CSRF-Token': csrfTokenValue }),
        Accept: 'text/html',
        Authorization: `Bearer ${sessionToken}`,
      },
    })
    return ensureNotAuthRedirect(response)
  },
  delete: async <T>(url: string) => {
    const sessionToken = await getSessionToken(app)
    const csrfTokenValue = csrfToken()
    const response = await axios.delete<T>(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(csrfTokenValue && { 'X-CSRF-Token': csrfTokenValue }),
        Accept: 'application/json',
        Authorization: `Bearer ${sessionToken}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
    return ensureNotAuthRedirect(response)
  },
}
export const fetcher = async <T>({
  queryKey,
}: QueryFunctionContext<QueryKeyT>): Promise<T> => {
  const [url, params] = queryKey
  try {
    // Tạo CancelToken cho mỗi request
    const cancelTokenSource = CancelToken.source()

    // Lưu CancelToken vào map để có thể cancel sau này
    cancelTokenMap.set(url, cancelTokenSource)

    const response = await axios.get<T>(url, {
      params: { ...params },
      cancelToken: cancelTokenSource.token,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${await getSessionToken(app)}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    })

    // Xóa CancelToken khỏi map sau khi request hoàn thành
    cancelTokenMap.delete(url)

    return response.data
  } catch (err) {
    // Xóa CancelToken khỏi map khi có lỗi
    cancelTokenMap.delete(url)

    // Kiểm tra xem có phải là cancel error không
    if (axios.isCancel(err)) {
      throw new Error('Request cancelled')
    }
    throw err // Thay vì return err, nên throw để React Query xử lý error
  }
}

const useGenericMutation = <T, S>(
  func: (data: T | S) => Promise<AxiosResponse<S>>,
  url: string,
  params?: object,
  updater?: ((oldData: T, newData: S) => T) | undefined,
) => {
  const queryClient = useQueryClient()
  return useMutation<AxiosResponse, AxiosError, T | S>(func, {
    onMutate: async data => {
      // Sử dụng helper function để tạo query key nhất quán
      const queryKey = createQueryKey(url, params)

      // Thực sự cancel HTTP requests đang chạy
      cancelRequest(url)

      // Cancel React Query state updates
      await queryClient.cancelQueries(queryKey)

      // Thử cancel tất cả queries có cùng URL pattern
      const urlPattern = url.split('?')[0] // Lấy base URL không có query params
      await queryClient.cancelQueries([urlPattern])

      // Lấy data hiện tại trước khi update
      const previousData = queryClient.getQueryData(queryKey)

      // Optimistic update
      queryClient.setQueryData<T>(queryKey, oldData => {
        const newData = updater ? updater(oldData as T, data as S) : (data as T)
        return newData
      })

      return previousData
    },
    onError: (_err, _, context) => {
      const queryKey = createQueryKey(url, params)
      queryClient.setQueryData(queryKey, context)
    },
    onSettled: () => {
      const queryKey = createQueryKey(url, params)
      // Invalidate để đảm bảo data được sync với server
      queryClient.invalidateQueries(queryKey)
    },
  })
}

export const useLoadMore = <T>(url: string | null, params?: object) => {
  const context = useInfiniteQuery<
    OrderLazyPropsInterface<T>,
    Error,
    OrderLazyPropsInterface<T>,
    QueryKeyT
  >(
    createQueryKey(url, params),
    ({ queryKey }) => fetcher({ queryKey, meta: undefined }),
    {
      getPreviousPageParam: (
        firstPage: OrderLazyPropsInterface<T>,
        _allPages: OrderLazyPropsInterface<T>[],
      ) => {
        // const lastPage = allPages[allPages.length - 1]
        // console.log(firstPage)
        const { pagy } = firstPage
        return pagy.prev ?? false
      },
      getNextPageParam: (lastPage: OrderLazyPropsInterface<T>) => {
        const { pagy } = lastPage
        return pagy.next ?? false
      },
    },
  )

  return context
}

export const useFetch = <T>(
  url: string | null,
  params?: object,
  config?: UseQueryOptions<T, Error, T, QueryKeyT>,
) => {
  const context = useQuery<T, Error, T, QueryKeyT>(
    createQueryKey(url, params),
    ({ queryKey }) => fetcher({ queryKey, meta: undefined }),
    {
      enabled: !!url,
      ...config,
    },
  )
  return context
}

// Hook mới để fetch data với response HTML - trả về axios response
export const useFetchHtml = <T>(
  url: string | null,
  params?: object,
  config?: UseQueryOptions<
    AxiosResponse<T>,
    Error,
    AxiosResponse<T>,
    QueryKeyT
  >,
) => {
  const context = useQuery<
    AxiosResponse<T>,
    Error,
    AxiosResponse<T>,
    QueryKeyT
  >(
    createQueryKey(url, params),
    async ({ queryKey }) => {
      const [url, params] = queryKey
      try {
        // Tạo CancelToken cho mỗi request
        const cancelTokenSource = CancelToken.source()

        // Lưu CancelToken vào map để có thể cancel sau này
        cancelTokenMap.set(url, cancelTokenSource)

        const response = await axios.get<T>(url, {
          params: { ...params },
          cancelToken: cancelTokenSource.token,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/html',
            Authorization: `Bearer ${await getSessionToken(app)}`,
            'X-Requested-With': 'XMLHttpRequest',
          },
        })

        // Xóa CancelToken khỏi map sau khi request hoàn thành
        cancelTokenMap.delete(url)

        return response // Trả về toàn bộ axios response
      } catch (err) {
        // Xóa CancelToken khỏi map khi có lỗi
        cancelTokenMap.delete(url)

        // Kiểm tra xem có phải là cancel error không
        if (axios.isCancel(err)) {
          throw new Error('Request cancelled')
        }
        throw err // Thay vì return err, nên throw để React Query xử lý error
      }
    },
    {
      enabled: !!url,
      ...config,
    },
  )
  return context
}
export const usePostData = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
) => {
  return useGenericMutation<T, S>(
    data => api.post_data<S>(url, data),
    url,
    params,
    updater,
  )
}

export const usePost = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
) => {
  return useGenericMutation<T, S>(
    data => api.post<S>(url, data),
    url,
    params,
    updater,
  )
}

export const useUpdate = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
) => {
  return useGenericMutation<T, S>(
    data => api.patch<S>(url, data),
    url,
    params,
    updater,
  )
}
export const useDelete = <T>(
  url: string,
  params?: object,
  updater?: (oldData: T, id: string | number) => T,
) => {
  return useGenericMutation<T, string | number>(
    id => api.delete(`${url}/${id}`),
    url,
    params,
    updater,
  )
}

// Hook mới để sử dụng POST với response HTML
export const usePostHtml = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
) => {
  return useGenericMutation<T, S>(
    data => api.post_html<S>(url, data),
    url,
    params,
    updater,
  )
}

// Hook mới để sử dụng PATCH với response HTML
export const useUpdateHtml = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
) => {
  return useGenericMutation<T, S>(
    data => api.patch_html<S>(url, data),
    url,
    params,
    updater,
  )
}

// Hook mới để sử dụng PUT với response HTML
export const usePutHtml = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
) => {
  return useGenericMutation<T, S>(
    data => api.put_html<S>(url, data),
    url,
    params,
    updater,
  )
}
